import { 
  signInAnonymously, 
  onAuthStateChanged 
} from "firebase/auth";
import { 
  doc, 
  setDoc, 
  updateDoc, 
  onSnapshot, 
  collection, 
  query, 
  where,
  serverTimestamp,
  getDoc,
  arrayUnion,
  arrayRemove
} from "firebase/firestore";
import { auth, db } from "./firebase";

/**
 * 실시간 위치 공유 서비스
 */
export const locationService = {
  // 1. 익명 로그인 처리
  async initAuth() {
    return new Promise((resolve, reject) => {
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          resolve(user);
        } else {
          try {
            const credential = await signInAnonymously(auth);
            resolve(credential.user);
          } catch (error) {
            reject(error);
          }
        }
      });
    });
  },

  // 1.5 새 방 만들기 (방장)
  async createGroup(userId, groupName, nickname) {
    if (!userId || !groupName || !nickname) throw new Error("입력 정보가 부족합니다.");
    
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);
    let myHostedCount = 0;

    if (userSnap.exists()) {
      const codes = userSnap.data().groupCodes || [];
      
      // 유효한(활성 및 24시간 미만) 그룹만 필터링하여 카운트
      const activeGroups = [];
      for (const code of codes) {
        const gSnap = await getDoc(doc(db, "groups", code));
        if (gSnap.exists() && gSnap.data().active) {
          const createdAt = gSnap.data().createdAt?.toDate();
          const now = new Date();
          // 24시간 초과 체크
          if (createdAt && (now - createdAt) > 24 * 60 * 60 * 1000) {
            continue; 
          }
          activeGroups.push(code);
          if (gSnap.data().hostId === userId) myHostedCount++;
        }
      }

      if (activeGroups.length >= 2) {
        throw new Error("최대 2개의 그룹까지만 참여/생성할 수 있습니다.");
      }
      if (myHostedCount >= 2) {
        throw new Error("직접 만든 그룹은 최대 2개까지만 가능하며 더 이상 참여할 수 없습니다.");
      }
    }

    const groupCode = Math.floor(100000 + Math.random() * 900000).toString();
    
    const groupRef = doc(db, "groups", groupCode);
    await setDoc(groupRef, {
      hostId: userId,
      groupName: groupName,
      active: true,
      createdAt: serverTimestamp(),
    });

    await setDoc(userRef, {
      uid: userId,
      nickname: nickname,
      groupCodes: arrayUnion(groupCode),
      lastSeen: serverTimestamp(),
    }, { merge: true });

    return { code: groupCode, groupName };
  },

  // 1.6 방 참가 (게스트)
  async joinGroup(userId, groupCode, nickname) {
    if (!userId || !groupCode || !nickname) throw new Error("입력 정보가 부족합니다.");
    
    const groupRef = doc(db, "groups", groupCode);
    const groupSnap = await getDoc(groupRef);
    
    if (!groupSnap.exists() || !groupSnap.data().active) {
      throw new Error("존재하지 않거나 이미 종료된 그룹입니다.");
    }

    // 24시간 경과 체크
    const createdAt = groupSnap.data().createdAt?.toDate();
    if (createdAt && (new Date() - createdAt) > 24 * 60 * 60 * 1000) {
      throw new Error("24시간이 경과하여 만료된 그룹입니다.");
    }

    const groupName = groupSnap.data().groupName || '알 수 없음';
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const codes = userSnap.data().groupCodes || [];
      let myHostedCount = 0;
      const activeGroups = [];

      for (const code of codes) {
        const gSnap = await getDoc(doc(db, "groups", code));
        if (gSnap.exists() && gSnap.data().active) {
          const createdAt = gSnap.data().createdAt?.toDate();
          const now = new Date();
          // 24시간 초과 체크 (만료된 방은 활성 그룹으로 카운트하지 않음)
          if (createdAt && (now - createdAt) > 24 * 60 * 60 * 1000) {
            continue;
          }
          activeGroups.push(code);
          if (gSnap.data().hostId === userId) myHostedCount++;
        }
      }

      if (activeGroups.length >= 2) throw new Error("이미 2개의 그룹에 참여 중입니다.");
      if (myHostedCount >= 2) throw new Error("이미 2개의 그룹을 생성하여 다른 그룹에 참여할 수 없습니다.");
      if (activeGroups.includes(groupCode)) throw new Error("이미 참여 중인 그룹입니다.");
    }

    await setDoc(userRef, {
      uid: userId,
      nickname: nickname,
      groupCodes: arrayUnion(groupCode),
      lastSeen: serverTimestamp(),
    }, { merge: true });
    
    return { code: groupCode, groupName };
  },

  // 1.7 방 나가기 또는 폭파
  async leaveOrCloseGroup(userId, groupCode) {
    if (!userId || !groupCode) return;
    
    const groupRef = doc(db, "groups", groupCode);
    const groupSnap = await getDoc(groupRef);
    
    if (groupSnap.exists() && groupSnap.data().hostId === userId) {
      await updateDoc(groupRef, { active: false });
    }

    const userRef = doc(db, "users", userId);
    await setDoc(userRef, {
      groupCodes: arrayRemove(groupCode)
    }, { merge: true });
  },

  // 2. 내 위치 업데이트
  async updateMyLocation(userId, location) {
    if (!userId) return;
    
    const userRef = doc(db, "users", userId);
    const data = {
      uid: userId,
      lat: location.lat,
      lng: location.lng,
      lastSeen: serverTimestamp(),
    };
    
    await setDoc(userRef, data, { merge: true });
  },

  // --- Phase 7: 1:1 위치 공유 (유료 서비스) ---
  
  // 1:1 공유 목록 구독
  subscribePrivateSharing(userId, callback) {
    const q = query(
      collection(db, "private_sharings"),
      where("participants", "array-contains", userId)
    );
    return onSnapshot(q, (snapshot) => {
      const sharings = [];
      snapshot.forEach((doc) => sharings.push({ id: doc.id, ...doc.data() }));
      callback(sharings);
    });
  },

  // 연락처로 공유 요청 보내기 (결제 여부 포함)
  async requestPrivateSharing(fromUserId, toPhone, fromNickname) {
    // 실제로는 결제 확인 로직이 들어감
    const sharingId = `ps_${Date.now()}`;
    const sharingRef = doc(db, "private_sharings", sharingId);
    
    await setDoc(sharingRef, {
      fromId: fromUserId,
      fromNickname: fromNickname,
      toPhone: toPhone,
      participants: [fromUserId], // 상대방 수락 전까지는 나만
      status: 'pending', // pending, active, inactive
      isPaid: true,
      createdAt: serverTimestamp(),
      sharedLocation: null
    });
    return sharingId;
  },

  // 요청 수락 (상대방)
  async acceptPrivateSharing(sharingId, myUserId) {
    const sharingRef = doc(db, "private_sharings", sharingId);
    await updateDoc(sharingRef, {
      participants: arrayUnion(myUserId),
      status: 'active',
      acceptedAt: serverTimestamp()
    });
  },

  // 상태 변경 (활성/비활성)
  async updateSharingStatus(sharingId, status) {
    const sharingRef = doc(db, "private_sharings", sharingId);
    await updateDoc(sharingRef, { status });
  },

  // 공유 삭제 (재결제 필요)
  async deleteSharing(sharingId) {
    const sharingRef = doc(db, "private_sharings", sharingId);
    await updateDoc(sharingRef, { status: 'deleted', deletedAt: serverTimestamp() });
  },

  // 비활성 상태에서 요청에 의한 한시적 위치 공유
  async shareOnce(sharingId, location) {
    const sharingRef = doc(db, "private_sharings", sharingId);
    await updateDoc(sharingRef, {
      sharedLocation: {
        ...location,
        sharedAt: new Date().toISOString()
      }
    });
  },

  // --- 데모 데이터 생성 (테스트용) ---
  async seedDemoData(userId) {
    if (!userId) return;
    const now = new Date();
    
    const demoCases = [
      {
        id: `demo_active_${userId}`,
        fromId: userId,
        fromNickname: '나',
        toPhone: '010-1111-2222(김철수)',
        participants: [userId, 'chulsu_id'],
        status: 'active',
        isPaid: true,
        createdAt: serverTimestamp(),
        sharedLocation: null // 활성 상태이므로 실시간 위치 참조 시뮬레이션용
      },
      {
        id: `demo_inactive_${userId}`,
        fromId: userId,
        fromNickname: '나',
        toPhone: '010-3333-4444(이영희)',
        participants: [userId],
        status: 'inactive',
        isPaid: true,
        createdAt: serverTimestamp(),
        sharedLocation: null
      },
      {
        id: `demo_once_${userId}`,
        fromId: userId,
        fromNickname: '나',
        toPhone: '010-5555-6666(박지민)',
        participants: [userId],
        status: 'inactive',
        isPaid: true,
        createdAt: serverTimestamp(),
        sharedLocation: {
          lat: 37.5700, // 종로 부근
          lng: 126.9833,
          sharedAt: now.toISOString()
        }
      }
    ];

    for (const demo of demoCases) {
      await setDoc(doc(db, "private_sharings", demo.id), demo);
    }
    
    // 활성 상태 멤버(김철수)의 실시간 위치 더미 데이터 생성
    await setDoc(doc(db, "users", "010-1111-2222(김철수)"), {
      uid: "010-1111-2222(김철수)",
      nickname: "김철수",
      lat: 37.5665, // 시청 부근
      lng: 126.9780,
      lastSeen: serverTimestamp()
    });
  },

  // --- 1:1 공유 멤버 실시간 위치 구독 ---
  subscribeMultipleUsers(userIds, callback) {
    if (!userIds || userIds.length === 0) return () => {};
    const q = query(
      collection(db, "users"),
      where("uid", "in", userIds)
    );
    return onSnapshot(q, (snapshot) => {
      const users = new Map();
      snapshot.forEach((doc) => users.set(doc.id, doc.data()));
      callback(users);
    });
  },

  // 별칭(이름) 변경
  async updatePrivateSharingName(sharingId, newName) {
    const sharingRef = doc(db, "private_sharings", sharingId);
    await updateDoc(sharingRef, { customName: newName });
  },

  // 특정 멤버에게 위치 공유 요청 (데모용 시뮬레이션)
  async requestLocationSharing(sharingId) {
    const sharingRef = doc(db, "private_sharings", sharingId);
    await updateDoc(sharingRef, {
      sharedLocation: {
        lat: 37.5665 + (Math.random() - 0.5) * 0.01, // 주변 랜덤 좌표
        lng: 126.9780 + (Math.random() - 0.5) * 0.01,
        sharedAt: new Date().toISOString()
      }
    });
  },

  // 4. 그룹 멤버 위치 실시간 구독
  subscribeGroupMembers(groupCode, callback) {
    const q = query(
      collection(db, "users"), 
      where("groupCodes", "array-contains", groupCode)
    );
    
    return onSnapshot(q, (snapshot) => {
      const members = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        members.push(data);
      });
      callback(members);
    });
  },

  // 5. 공지 업데이트
  async updateGroupNotice(groupCode, notice) {
    const groupRef = doc(db, "groups", groupCode);
    await updateDoc(groupRef, { notice });
  },

  // 그룹 정보 및 상태(폭파 여부, 공지 등) 구독
  subscribeGroupStatus(groupCode, callback) {
    const groupRef = doc(db, "groups", groupCode);
    return onSnapshot(groupRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        callback({
          isActive: data.active === true,
          notice: data.notice || '',
          createdAt: data.createdAt ? data.createdAt.toDate().getTime() : null
        });
      } else {
        callback({ isActive: false, notice: '' });
      }
    });
  }
};
